import { isAfter, isBefore } from 'date-fns';

import {
  IKnmErknmFormViolatedRequirementsMandatory,
  IKnmErknmFormViolatedRequirementsNpa,
} from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import {
  IKnmErknmFormViolation,
  IKnmErknmFormViolationFix,
  IViolationFixPlanDateItem,
  IViolationFixPlanExplanationItem,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { formatDate, parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ViolationFixMaterialTypesEnum } from 'APIPackage/KnmModule/enums/ViolationFixMaterialTypesEnum';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { ViolationFixMethodsEnum } from 'APIPackage/KnmModule/enums/ViolationFixMethodsEnum';
import { getArrayError } from 'CorePackage/MainModule/common/getArrayError';

import { makeRequiredQuillEditorDelta } from '../../../CorePackage/MainModule/validators/isRequiredQuillEditorDelta';
import { IErknmFormAgreement } from '../interfaces/IErknmFormAgreement';
import { ViolationStatusesEnum } from '../../../APIPackage/KnmModule/enums/ViolationStatusesEnum';

export const makeRequiredArray = (errorMsg: string) => (value: unknown) => {
  const error = makeRequired(errorMsg)(value);

  return error ? getArrayError(error) : undefined;
};

const getFixPlanExplanationsValidate = (
  fixPlanExplanations: IViolationFixPlanExplanationItem[] | null,
): FormErrors<IViolationFixPlanExplanationItem>[] | undefined | string => {
  return fixPlanExplanations?.map(dateItem => {
    if (!dateItem || !dateItem.value) {
      return { value: REQUIRED_ERROR };
    }

    return { value: undefined };
  });
};

const getViolationFixPlanDateValidate = (
  dates: IViolationFixPlanDateItem[] | null,
  minFixPlanDate: string | undefined,
): FormErrors<IViolationFixPlanDateItem>[] | undefined | string => {
  return dates?.map((dateItem, idx) => {
    const date = dateItem.value
      ? parseDate({
          date: dateItem.value,
          parseFormat: DATE_FORMAT_LONG_FNS,
        })
      : undefined;

    if (!date) {
      return { value: REQUIRED_ERROR };
    }

    if (!dateItem.isNew) {
      return { value: undefined };
    }

    const currentDate = parseDate({
      date: formatDate({ date: new Date() || '', dateFormat: DATE_FORMAT_LONG_FNS }),
      parseFormat: DATE_FORMAT_LONG_FNS,
    });

    if (currentDate && isBefore(date, currentDate)) {
      return { value: 'Дата устранения должна быть больше или равна текущей даты' };
    }

    const prevDateValue = dates[idx - 1]?.value;
    const prevDate = prevDateValue
      ? parseDate({
          date: prevDateValue,
          parseFormat: DATE_FORMAT_LONG_FNS,
        })
      : undefined;

    if (prevDate && date && !isAfter(date, prevDate)) {
      return { value: 'Дата должна быть больше предыдущей' };
    }

    const minDate = minFixPlanDate ? parseDate({ date: minFixPlanDate, parseFormat: DATE_FORMAT_LONG_FNS }) : undefined;

    if (minDate && isBefore(date, minDate)) {
      return { value: 'Дата устранения должна быть больше или равна даты решения' };
    }

    return { value: undefined };
  });
};

const getViolationFixesValidate = (
  violation: IKnmErknmFormViolation,
  isDecisionMode: boolean,
  isSigned: boolean | undefined,
): FormErrors<IKnmErknmFormViolationFix>[] | undefined | string => {
  const violationPlanFixMethodValues =
    violation.fixPlanMethods?.map(option => option.value?.value as ViolationFixMaterialTypesEnum) || [];

  const violationResolved = violation.status === ViolationStatusesEnum.FIXED || ViolationStatusesEnum.APPROVING;

  return violation.fixes?.map(fixItem => {
    const hasMaterialWithExplanation: boolean = !!fixItem.type?.some(
      typeItem => typeItem.value?.value === ViolationFixMaterialTypesEnum.EXPLANATION,
    );

    let typeValidation:
      | string
      | boolean
      | undefined
      | string[]
      | {
          value: undefined | string;
        }[];

    const isFieldRequired = !fixItem.type?.length && !violationResolved && isSigned === false;

    if (violation.repeatCorrected?.value || violation.repeatCorrectedInEvent?.value) {
      typeValidation = isFieldRequired ? getArrayError(REQUIRED_ERROR) : undefined;
    } else {
      typeValidation = isFieldRequired
        ? getArrayError(REQUIRED_ERROR)
        : fixItem.type?.map(type =>
            violationPlanFixMethodValues.includes(type.value?.value as ViolationFixMaterialTypesEnum) || !isDecisionMode
              ? { value: undefined }
              : { value: 'Несовместимо с другими полями' },
          );
    }

    return {
      type: typeValidation,
      explanationMaterialReactQuillEditor:
        hasMaterialWithExplanation && fixItem.isNew
          ? makeRequiredQuillEditorDelta(REQUIRED_ERROR)(fixItem.explanationMaterialReactQuillEditor)
          : undefined,
      method: makeRequired(REQUIRED_ERROR)(fixItem.method),
    };
  });
};

const getRequirementViolationsValidate = ({
  agreement,
  inDecisionBody,
  isDecisionMode,
  isPaperless,
  isRepeatValidationChecking,
  isSigned,
  minFixPlanDate,
}: {
  agreement: IErknmFormAgreement | undefined;
  inDecisionBody: boolean | undefined;
  isDecisionMode: boolean;
  isPaperless: boolean | undefined;
  isRepeatValidationChecking: boolean | undefined;
  isSigned: boolean | undefined;
  minFixPlanDate: string | undefined;
}) => (violations: IKnmErknmFormViolation[] | null): FormErrors<IKnmErknmFormViolation>[] | undefined | string[] => {
  return composeValidators(
    makeRequiredArray('Раздел не может быть пустым'),
    (values: IKnmErknmFormViolation[] | null) =>
      values?.map(item => {
        const isUnfixable = !!item.isUnfixable?.value;
        const isFixedOnPlace = !!item.isFixedOnPlace?.value;
        const isFixed = !!item.isFixed?.value;
        const isRepeatCorrected = !!item.repeatCorrected?.value;
        const isRepeatCorrectedInEvent = !!item.repeatCorrectedInEvent?.value;
        const isAppealed = !!item.isAppealed?.value;
        const hasMobileAppInfo = item.fixes?.find(fix => fix.method?.value === ViolationFixMethodsEnum.MOBILE_APP_INFO);

        return {
          title: makeRequired(REQUIRED_ERROR)(item.title),
          descriptionReactQuillEditor: makeRequiredQuillEditorDelta(REQUIRED_ERROR)(item.descriptionReactQuillEditor),
          isFixedOnPlace: isUnfixable && isFixedOnPlace ? 'Несовместимо с другими полями' : undefined,
          isFixed: (isFixed && isAppealed) || (isFixed && isFixedOnPlace) ? 'Несовместимо с другими полями' : undefined,
          fixPlanExplanations: !(isPaperless && isDecisionMode)
            ? undefined
            : getFixPlanExplanationsValidate(item.fixPlanExplanations),
          fixPlanExplanationsRequired:
            isDecisionMode && isPaperless ? makeRequired(REQUIRED_ERROR)(item.fixPlanExplanations) : undefined,
          fixPlanDates: isDecisionMode ? getViolationFixPlanDateValidate(item.fixPlanDates, minFixPlanDate) : undefined,
          fixPlanDatesRequired:
            isDecisionMode && !isPaperless && !isFixed && !isAppealed
              ? makeRequired(REQUIRED_ERROR)(item.fixPlanDates)
              : undefined,
          fixPlanMethodsRequired:
            isDecisionMode && !isFixed && !isAppealed ? makeRequired(REQUIRED_ERROR)(item.fixPlanMethods) : undefined,
          fixFactMethod:
            ((isDecisionMode && isPaperless) || !!agreement) && isFixed && !hasMobileAppInfo
              ? makeRequired(REQUIRED_ERROR)(item.fixFactMethod)
              : undefined,
          fixes:
            isDecisionMode || !!agreement || isFixed || (isRepeatValidationChecking && isRepeatCorrected)
              ? getViolationFixesValidate(item, isDecisionMode || !!agreement, isSigned)
              : undefined,
          dateResolvedByAgreement:
            !!agreement &&
            agreement.approvalOfAgreement?.value &&
            agreement?.status !== AgreementStatusEnum.CANCELED &&
            agreement?.status !== AgreementStatusEnum.DENIED &&
            agreement?.status !== AgreementStatusEnum.TERMINATED &&
            agreement?.status !== AgreementStatusEnum.NOT_COMPLETED
              ? makeRequired(REQUIRED_ERROR)(item.dateResolvedByAgreement)
              : undefined,
          repeatCorrected: composeValidators(
            !inDecisionBody && isRepeatValidationChecking ? makeRequired(REQUIRED_ERROR) : () => undefined,
            () => (isRepeatCorrectedInEvent && isRepeatCorrected ? 'Несовместимо с другими полями' : undefined),
          )(item.repeatCorrected),
          repeatCorrectedInEvent:
            isRepeatCorrectedInEvent && isRepeatCorrected ? 'Несовместимо с другими полями' : undefined,
        };
      }),
  )(violations);
};

export const getViolatedMandatoryRequirementsValidator = ({
  isDecisionMode,
  isSigned,
  isProdReady,
  isValidateRequiredViolatedMandatory,
  agreement,
  inDecisionBody,
  isPaperless,
  isRepeatValidationChecking,
  minFixPlanDate,
}: {
  isDecisionMode: boolean;
  isSigned: boolean | undefined;
  isProdReady: boolean;
  isValidateRequiredViolatedMandatory: boolean;
  agreement?: IErknmFormAgreement;
  inDecisionBody?: boolean;
  isPaperless?: boolean;
  isRepeatValidationChecking?: boolean;
  minFixPlanDate?: string | undefined;
}) => (
  mandatoryValues: IKnmErknmFormViolatedRequirementsNpa[] | undefined,
): FormErrors<IKnmErknmFormViolatedRequirementsNpa>[] | undefined | string[] => {
  if (!mandatoryValues?.length) {
    return undefined;
  }

  const violatedMandatoryIds: string[] = mandatoryValues
    .map(item =>
      (item.violatedMandatory || []).map(
        violatedItem => `${item.npa.id}/${item.structuralUnit.id}/${violatedItem.mandatory.id}`,
      ),
    )
    .flat();

  return mandatoryValues.map(item => {
    const violatedMandatoryRequiredError: string | undefined = isValidateRequiredViolatedMandatory
      ? makeRequired('Раздел не может быть пустым')(item.violatedMandatory)
      : undefined;

    return {
      violatedMandatory: violatedMandatoryRequiredError
        ? getArrayError(violatedMandatoryRequiredError)
        : (item.violatedMandatory || []).map<FormErrors<IKnmErknmFormViolatedRequirementsMandatory>>(violatedItem => {
            const isDuplicate =
              violatedMandatoryIds.filter(
                idItem => idItem === `${item.npa.id}/${item.structuralUnit.id}/${violatedItem.mandatory.id}`,
              ).length > 1;

            return {
              mandatory: isDuplicate ? 'Поле должно содержать уникальное значение' : undefined,
              category: isValidateRequiredViolatedMandatory
                ? makeRequired('Поле не может быть пустым')(violatedItem.category)
                : undefined,
              violations: isProdReady
                ? getRequirementViolationsValidate({
                    agreement,
                    inDecisionBody,
                    isDecisionMode,
                    isPaperless,
                    isRepeatValidationChecking,
                    isSigned,
                    minFixPlanDate,
                  })(violatedItem.violations)
                : undefined,
            };
          }),
    };
  });
};
